import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Plug, RefreshCw, Trello, Beaker, Link2Off } from "lucide-react";
import OnboardingLayout from "@/components/OnboardingLayout";
import { useBusiness } from "@/context/BusinessContext";
import { api, apiError } from "@/lib/api";
import { Button, Spinner, Skeleton } from "@/components/ui";

export default function OnboardingIntegrations() {
  const { active, loading } = useBusiness();
  const nav = useNavigate();
  const [state, setState] = React.useState(null);
  const [boards, setBoards] = React.useState([]);
  const [lists, setLists] = React.useState([]);
  const [boardId, setBoardId] = React.useState("");
  const [listId, setListId] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [polling, setPolling] = React.useState(false);
  const pollRef = React.useRef(null);

  const bid = active?.id;

  const loadState = React.useCallback(async () => {
    if (!bid) return;
    const { data } = await api.get(`/businesses/${bid}/integrations/trello`);
    setState(data);
    if (data.board) setBoardId(data.board.id);
    if (data.list) setListId(data.list.id);
    if (data.connected) {
      try {
        const b = await api.get(`/businesses/${bid}/integrations/trello/boards`);
        setBoards(b.data.boards || []);
        if (data.board) {
          const l = await api.get(`/businesses/${bid}/integrations/trello/boards/${data.board.id}/lists`);
          setLists(l.data.lists || []);
        }
      } catch (e) { /* boards load best-effort */ }
    }
  }, [bid]);

  React.useEffect(() => {
    if (!loading && !active) nav("/onboarding/business", { replace: true });
    else if (active) loadState();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loading, active, nav, loadState]);

  if (loading || !active) return null;

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${bid}/integrations/trello/connect`);
      if (data.redirect_url) window.open(data.redirect_url, "_blank", "width=620,height=760");
      startPolling();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startPolling = () => {
    setPolling(true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.post(`/businesses/${bid}/integrations/trello/status`);
        if (data.status === "ACTIVE") {
          clearInterval(pollRef.current); setPolling(false);
          toast.success("Trello connected");
          await loadState();
        }
      } catch (e) { /* keep polling */ }
    }, 3000);
  };

  const onBoard = async (id) => {
    setBoardId(id); setListId(""); setLists([]);
    if (!id) return;
    try {
      const { data } = await api.get(`/businesses/${bid}/integrations/trello/boards/${id}/lists`);
      setLists(data.lists || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    const board = boards.find((b) => b.id === boardId);
    const list = lists.find((l) => l.id === listId);
    if (!board || !list) return;
    setBusy(true);
    try {
      await api.post(`/businesses/${bid}/integrations/trello/select`, {
        board_id: board.id, board_name: board.name, list_id: list.id, list_name: list.name,
      });
      toast.success("Routing target saved");
      await loadState();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const testCard = async () => {
    setBusy(true);
    try {
      await api.post(`/businesses/${bid}/integrations/trello/test-card`);
      toast.success("Test card created in Trello");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete(`/businesses/${bid}/integrations/trello`);
      setBoards([]); setLists([]); setBoardId(""); setListId("");
      await loadState();
      toast.success("Disconnected");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const connected = state?.connected;
  const notConfigured = state && state.composioConfigured === false;
  const selectCls = "input w-full";

  return (
    <OnboardingLayout current="integrations" title="Connect where requests go"
      secondaryAction={{ label: "Skip for now", onClick: () => nav("/onboarding/playbook"), variant: "ghost", testId: "integrations-skip" }}
      primaryAction={{ label: "Continue", onClick: () => nav("/onboarding/playbook"), testId: "integrations-continue", icon: <ArrowRight size={16} /> }}>
      {!state ? (
        <div className="w-full" data-testid="trello-loading">
          <div className="flex items-center gap-3 mb-5">
            <Skeleton style={{ width: 40, height: 40 }} />
            <div className="flex-1">
              <Skeleton style={{ height: 14, width: 120, marginBottom: 6 }} />
              <Skeleton style={{ height: 10, width: 80 }} />
            </div>
          </div>
          <Skeleton style={{ height: 38, width: 170 }} />
        </div>
      ) : (
      <div className="w-full">
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center justify-center" style={{ width: 40, height: 40, background: "var(--s2)", border: "1px solid var(--border)" }}>
            <Trello size={20} style={{ color: "var(--accent-2)" }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm">Trello</div>
            <div className="mono text-[11px]" style={{ color: "var(--text-muted)" }}>via Composio</div>
          </div>
          {connected ? (
            <span className="tag tag-success flex items-center gap-1" data-testid="trello-status-badge"><Check size={12} /> Connected</span>
          ) : (
            <span className="tag" data-testid="trello-status-badge">{polling ? "Waiting…" : "Not connected"}</span>
          )}
        </div>

        {notConfigured && (
          <div className="p-3 mb-4 text-xs" style={{ background: "var(--s2)", border: "1px dashed var(--border-strong)", color: "var(--text-2)" }} data-testid="composio-unconfigured">
            Composio isn't configured on the server yet. Add <span className="mono">COMPOSIO_API_KEY</span> and this will go live instantly.
          </div>
        )}

        {!connected && (
          <div className="space-y-3">
            <Button variant="accent2" onClick={connect} disabled={busy || notConfigured} data-testid="trello-connect-btn">
              {busy ? <Spinner size={14} /> : <Plug size={15} />} Connect Trello
            </Button>
            {polling && (
              <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }} data-testid="trello-waiting">
                <RefreshCw size={13} className="animate-spin" /> Finish the Trello login in the popup. We'll detect it automatically.
              </div>
            )}
          </div>
        )}

        {connected && (
          <div className="space-y-4" data-testid="trello-connected-panel">
            <div>
              <div className="heading-eyebrow mb-1.5">Board</div>
              <select className={selectCls} value={boardId} onChange={(e) => onBoard(e.target.value)} data-testid="trello-board-select">
                <option value="">Select a board…</option>
                {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <div className="heading-eyebrow mb-1.5">List (where new requests land)</div>
              <select className={selectCls} value={listId} onChange={(e) => setListId(e.target.value)} disabled={!boardId} data-testid="trello-list-select">
                <option value="">Select a list…</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="accent2" onClick={save} disabled={busy || !boardId || !listId} data-testid="trello-save-btn">
                {busy ? <Spinner size={14} /> : <Check size={15} />} Save routing target
              </Button>
              <Button variant="ghost" onClick={testCard} disabled={busy || !state?.list?.id} data-testid="trello-test-btn">
                <Beaker size={15} /> Create test card
              </Button>
              <Button variant="ghost" onClick={disconnect} disabled={busy} data-testid="trello-disconnect-btn">
                <Link2Off size={15} /> Disconnect
              </Button>
            </div>
            {state?.list?.id && (
              <p className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="trello-routing-summary">
                New requests route to <span className="mono" style={{ color: "var(--accent-2)" }}>{state.board?.name} › {state.list?.name}</span>.
              </p>
            )}
          </div>
        )}
      </div>
      )}
    </OnboardingLayout>
  );
}
